import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import ShipBayPlanTemplateSelect from "view/ships/components/selects/ShipBayPlanTemplateSelect";
import { createBayplanTemplateSchema, CreateBayplanTemplateSchemaInput, CreateBayplanTemplateSchemaOutput } from "view/ships/utils/ship.schema";

import { BargeType } from "__generated__/enums";
import { DeleteShipBayPlanTemplateDocument, SaveShipBayPlanTemplateDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";

interface CreateTemplateDialogProps {
	open: boolean;
	onClose: () => void;
	bayPlan: {
		rows: number;
		bays: number;
		tiers: number;
	};
}

const CreateBayPlanTemplateDialog = ({ open, onClose, bayPlan }: CreateTemplateDialogProps) => {

	const defaultValues = {
		name: "",
		overrideTemplate: null,
	};

	const { control, handleSubmit, formState, reset } = useForm<CreateBayplanTemplateSchemaInput, unknown, CreateBayplanTemplateSchemaOutput>({
		resolver: zodResolver(createBayplanTemplateSchema),
		defaultValues: defaultValues,
	});

	const [ saveTemplate, { loading: saveLoading } ] = useMutation(SaveShipBayPlanTemplateDocument);
	const [ deleteTemplate, { loading: deleteLoading } ] = useMutation(DeleteShipBayPlanTemplateDocument);

	const loading = saveLoading ?? deleteLoading;

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const beforeSave = (data: CreateBayplanTemplateSchemaOutput) => {
		if (data.overrideTemplate) {
			deleteTemplate({
				variables: {
					id: data.overrideTemplate.id,
				},
			}).finally(() => {
				save(data);
			});
		} else {
			save(data);
		}
	};

	const save = (data: CreateBayplanTemplateSchemaOutput) => {
		saveTemplate({
			variables: {
				input: {
					id: undefined,
					name: data.name,
					shipBargeType: BargeType.ContainerShip,
					rows: bayPlan.rows,
					bays: bayPlan.bays,
					tiers: bayPlan.tiers,
					bayPlan: JSON.stringify({ ...bayPlan, shipBargeType: BargeType.ContainerShip }),
				},
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			handleOnClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CREATE_TEMPLATE" })}
			form='createTemplateForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(beforeSave)(event)} id="createTemplateForm">
				<FormGroup label={intl.formatMessage({ id: "TEMPLATES_NAME" })} overflow="hidden" required>
					<Controller
						name='name'
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "OVERWRITE_TEMPLATE" })} overflow="hidden">
					<Controller
						name='overrideTemplate'
						control={control}
						render={({ field }) => (
							<ShipBayPlanTemplateSelect
								{...field}
								rows={bayPlan.rows}
								bays={bayPlan.bays}
								tiers={bayPlan.tiers} />
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default CreateBayPlanTemplateDialog;
